/** Shapes returned by the API. Kept in sync by hand with backend/seldon_tasks (small surface). */

export type Role = 'sme' | 'engineer'
/** Who must act in a stage. The authoring agent owns one but is never a principal. */
export type StageOwner = Role | 'agent'
export type Action = 'forward' | 'send_back' | 'return_to_expert'
export type FileKind = 'final_output' | 'video' | 'state' | 'initial_state' | 'model_results' | 'example_positive' | 'example_negative' | 'attachment'
export type FileStatus = 'pending' | 'uploaded' | 'invalid'

/** `color` is the server-assigned background of an emoji avatar, unique across users. */
export interface GitHubAccount { id: number; login: string; name?: string; avatar_url?: string; linked_at?: string }
export interface Avatar { kind: 'emoji' | 'image' | 'lego'; value: string; color?: string }

export interface User {
  peer_reviewer?: boolean
  user_id: string
  email: string
  role: Role
  name?: string
  domain?: string
  areas?: string[]
  onboarding_version?: number
  tutorial?: { step: number; status: 'active' | 'completed' | 'skipped'; version?: number }
  programs?: string[]
  bio?: string
  avatar?: Avatar | null
  /** Developers: the GitHub account their commits are matched to (by numeric id). */
  github?: GitHubAccount | null
  created_at: string
}

/** Someone a page names (a feedback writer, a reviewer): their public profile, by user id. */
export type Person = Pick<User, 'user_id' | 'name' | 'email' | 'role' | 'avatar' | 'bio' | 'created_at' | 'programs' | 'domain' | 'areas'>
/** `people` on task and review pages: everyone the page names, keyed by user id (backend people.py). */
export type People = Record<string, Person>

/** Experts must have a picture, a bio and at least one program before using the portal. */
export const profileAreas = (u: Pick<User, 'areas' | 'domain'>) => u.areas ?? (u.domain ? [u.domain] : [])
export const profileComplete = (u: User | null | undefined) => Boolean(u && u.avatar?.value && u.bio?.trim() && (u.programs?.length ?? 0) > 0 && (u.onboarding_version !== 1 || profileAreas(u).length > 0))

export interface Brief {
  instance_cases?: string
  instance_changes?: string
  instance_constraints?: string
  instance_data?: string
  instance_rubric?: string
  what: string
  requester: string
  use: string
  starting_point: string
  finished_state: string
  judgment_decisions: string
  rules: string
  implicit_expectations: string
  acceptable_variation: string
  common_failures: string[]
  /** Former question 11; only present on tasks created before the rubric replaced it. */
  quality_check?: string
  time_and_difficulty: string
  open_questions?: string
}

/** Ordered display groups. IDs continue to identify the same grading criteria. */
export interface RubricSection { section_id: string; title: string; criterion_ids: string[] }
export type RubricLayout = RubricSection[]

export interface RubricCriterion {
  criterion_id: string
  criterion: string
  rationale?: string
  source?: 'instruction' | 'input' | 'domain_norm' | 'safety_rule'
  visibility?: 'explicit' | 'implicit'
  judgment?: 'objective' | 'subjective'
  role: 'required' | 'bonus' | 'penalty'
  pass_gate?: boolean
  evidence?: string
  decision_anchor?: string
}

export interface Feedback {
  from_role: Role
  /** The writer's user id; absent when an old record's writer could not be resolved. */
  from_id?: string
  from_email: string
  message: string
  from_stage: number
  to_stage: number
  at: string
}

export interface BuildState {
  verifier_uploaded?: boolean
  verifier_location?: string
  qcow2_uploaded?: boolean
  qcow2_location?: string
  notes?: string
  validation?: Record<string, { positive_passed: boolean; negative_caught: boolean; evidence?: string }>
}

export type ScoredType = 'bool' | 'int' | 'float' | 'string'
export type Scalar = boolean | number | string
export type ExpectedValue =
  | { kind: 'equals'; value: Scalar }
  | { kind: 'one_of'; values: Scalar[] }
  | { kind: 'range'; min?: number; max?: number }
  | { kind: 'tolerance'; target: number; tolerance: number }
  | { kind: 'regex'; pattern: string }
export interface TypedComparison {
  scored_type?: ScoredType
  state_path?: string
  actual?: Scalar | null
  presence_path?: string
  present?: boolean
  expected?: ExpectedValue
  scoring_description?: string
}

/** One published check: what it verifies, which rubric criteria it grades, and its weight. */
export interface ImplementedCheck extends TypedComparison {
  id: string
  label: string
  origin: 'expert' | 'engineer'
  /** Expert rubric criterion ids this check grades; empty only for a builder-added check. */
  rubric_criterion_ids: string[]
  component: string
  evaluator: string
  /** The intermediate verification outputs folded under this one label. */
  inputs?: string[]
  evidence_domain: string
  weight: number
  required_for_pass: boolean
  parameters?: Record<string, unknown>
  /** Where the builder departed from the expert's wording, and why. */
  note?: string
}
/** `rubric-map.json` as published inside the task package. */
export interface ImplementedRubricSpec {
  schema_version: number
  aggregation: {
    method: 'weighted' | 'binary' | 'gated_weighted' | 'weighted_malus'
    components: Record<string, number>
    reward_summary: string
    failed_reward_cap?: number
    baseline?: string
    malus_components?: string[]
  }
  checks: ImplementedCheck[]
}

/** A science pipeline step that stopped and waits for a developer. Experts get only its kind. */
export type ScienceException = { kind: string; job_id?: string; error?: string; at?: string }

export type SourceTemplate = { template_id: string; title?: string }

export interface Task {
  kind?: 'scientific_workflow'
  scientific_example_of?: string
  scientific_template_id?: string
  source_workflow_id?: string
  scientific_authoring?: { example_ids: string[]; import_job_id?: string; template_task_id?: string; builder?: { job_id: string }
    research?: { intake_id: string; intake_title?: string } }
  /** Developer list rows of a compiled workflow: its tasks. */
  members?: { task_id: string; title: string; status: string }[]
  /** List rows of a research-approved workflow: the template it came from. */
  source_template?: SourceTemplate
  /** A physics template: one task in the lists, reviewed on the template page. */
  collection?: import('./scientific').TemplateCollection
  /** A task of a physics template, hidden from the lists. */
  scientific?: import('./scientific').ScienceState
  display_stage?: number
  /** A compiled physics workflow waiting in the optional expert review before its rollouts. */
  pre_rollout_review?: boolean
  programs?: string[]
  build_waiting?: string | null
  lifecycle?: 'active' | 'retired'
  retirement?: { actor: { user_id: string; email: string; role: string }; at: string; feedback: string; stage: number }
  workflow_version?: number
  review_round_id?: string
  returned_quality_round_id?: string | null
  coach_review?: { review_id?: string; status: string; reason?: string; stale?: boolean; current_fingerprint?: string }
  publication?: { status: string; round_id?: string; error?: string; attempts?: number; last_attempt_at?: string }
  authoring?: {
    job_id: string; status: string; blocker?: string; cancel_reason?: string
    delivery_error?: { code: string; message: string } | null
    candidate?: { repository: string; commit: string; program: string; task_id: string }
    final?: { repository: string; commit: string; program: string; task_id: string }
  }
  authoring_submission?: {
    handoff?: { implementation_notes?: string; implemented_rubric?: ImplementedRubricSpec }
    pull_request: { number: number; url: string; commit?: string }
    branch?: string; summary?: string; merged?: boolean
    submitted_at?: string; merged_at?: string
  }
  authoring_review_id?: string | null
  science_exception?: ScienceException | null
  revision?: number
  active_review_id?: string | null
  refinement_started?: boolean
  expert_action_needed?: boolean
  retired_criterion_ids?: string[]
  task_id: string
  title: string
  program: string
  prompt: string
  brief: Brief
  tags?: string[]
  request_id?: string | null
  /** The task this one continues (same expert, already past the draft review), if any. */
  parent_task_id?: string | null
  stage: number
  stage_entered_at: string
  stage_info: { stage: number; label: string; owner: StageOwner | null }
  sme_id: string
  sme_email: string
  engineer_id?: string | null
  engineer_email?: string | null
  verify: boolean
  rubric: RubricCriterion[]
  rubric_layout?: RubricLayout
  review_notes?: string
  build?: BuildState
  qc?: { rollouts_run?: number; pass_rate?: number; notes?: string }
  /** Engineer task list only: verdicts on the current quality-check round. */
  qc_signals?: QcSignals
  last_feedback?: Feedback | null
  /** The draft as the expert submitted it (taken at 0 -> 1, cleared when the expert edits again). */
  draft_snapshot?: DraftSnapshot | null
  submission: Submission
  created_at: string
  updated_at: string
}

export type QcVerdict = 'ready' | 'needs_changes'
export interface QcSignals {
  agent: QcVerdict | 'pending' | 'running' | 'failed' | 'unavailable'
  expert: { verdict: QcVerdict | 'pending'; ready: number; needs_changes: number }
}

export interface DraftSnapshot {
  title: string
  program: string
  prompt: string
  brief: Brief
  rubric: RubricCriterion[]
  rubric_layout?: RubricLayout
  submitted_at: string
}

export interface FileItem {
  review_id?: string
  version_id?: string
  file_id: string
  task_id: string
  kind: FileKind
  filename: string
  content_type: string
  size: number
  s3_key: string
  status: FileStatus
  criterion_id?: string | null
  description?: string
  stage_uploaded: number
  created_at: string
  uploaded_at?: string | null
  validation?: FileValidation | null
}

/**
 * The task's single submission. The execution itself is two FILE items (kind `video`: the screen
 * recording; kind `state`: the backend-state tracking export); only the expert's notes live here,
 * plus `empty_start`: the task begins from a blank document, so no initial state is required.
 */
export interface Submission {
  result?: string
  notes?: string
  alternative_approaches?: string
  empty_start?: boolean
  updated_at?: string
}

export interface TaskEvent {
  workflow_version?: number
  type: string
  at: string
  actor_id?: string
  actor_email?: string
  actor_role?: StageOwner
  message?: string
  action?: Action
  from_stage?: number
  to_stage?: number
  [k: string]: unknown
}

export interface AvailableAction {
  action: Action
  to_stage: number
  blockers: string[]
}

export interface RubricReview {
  kind?: 'authoring_blocker'
  review_id: string
  status: 'open' | 'returned' | 'superseded'
  requested_at: string
  requested_by: string
  requested_by_id?: string
  notes: string
  file_ids: string[]
  rubric_before: RubricCriterion[]
  rubric_layout_before?: RubricLayout
  rubric_layout_after?: RubricLayout | null
  rubric_after?: RubricCriterion[] | null
  response?: string
  returned_at?: string
  returned_by?: string
  returned_by_id?: string
}

export interface RefinementPermissions {
  turn: Role
  can_edit_rubric: boolean
  can_request_review: boolean
  can_respond: boolean
  can_upload_results: boolean
  can_edit_examples: boolean
  can_validate: boolean
  handoff_blockers: string[]
}

export interface QualityChanges {
  rebuild_required: boolean
  peer_review_stale: boolean
  changes: { field: string; label: string; diff: string; before?: unknown; after?: unknown }[]
  send_back_message: string
}

export interface TaskDetail {
  quality_changes?: QualityChanges | null
  lifecycle_permissions?: { can_retire: boolean; can_restore: boolean; can_resume_build: boolean }
  content_permissions?: { can_edit: boolean }
  reviews?: RubricReview[]
  refinement?: RefinementPermissions
  task: Task
  people?: People
  /** The starting task this one continues, resolved by the API; null when the task stands alone. */
  parent?: Task | null
  files: FileItem[]
  events: TaskEvent[]
  actions: AvailableAction[]
}

export interface SmeSummary extends User {
  task_counts: Record<string, number>
  task_total: number
}

export interface EngOverview {
  counts_by_stage: Record<string, number>
  total: number
  my_tasks: Task[]
  unassigned: Task[]
  stages: { stage: number; label: string; owner: StageOwner | null }[]
}

/** Immutable content edits; snapshots are fetched only when history is opened. */
export interface ContentEdit {
  version_id: string
  at: string
  actor: { user_id: string; email: string; role: Role }
  stage: number
  changed_fields: ('brief' | 'prompt')[]
}
export interface ContentHistoryPage { versions: ContentEdit[]; cursor: string | null }
export interface ContentVersion extends ContentEdit {
  before: { brief: Brief; prompt: string }
  after: { brief: Brief; prompt: string }
}

// ----------------------------------------------------------------------------- rubric coach

export type CoachMessageRole = 'expert' | 'coach' | 'coach_note' | 'activity'
export type CoachThreadStatus = 'idle' | 'in_progress' | 'stalled' | 'failed'

export interface CoachThread {
  status: CoachThreadStatus
  message_count: number
  created_at: string
  last_step_at: string | null
  /** What the coach is doing right now ("Reading knowledge/protocol.md", "Writing the reply"); null when idle. */
  current_step?: string | null
  model: string
  can_retry_sync?: boolean
}

export interface CoachAttachment { file_id: string; filename: string; size: number; content_type: string; status: string }
export interface CoachSend { message: string; request_id?: string; draft?: CoachDraft; attachment_ids?: string[] }

/** `seq` is a zero-padded string, so ordering by plain string comparison is safe. */
export interface CoachMessage {
  seq: string
  role: CoachMessageRole
  text: string
  at: string
  draft_included?: boolean
  turn_id?: string
  attachments?: CoachAttachment[]
}

export interface CoachPermissions { can_chat: boolean; can_export: boolean }

/** One page of `GET /tasks/{id}/coach`; `cursor` is the `before=` value for the next older page. */
export interface CoachPage {
  enabled: boolean
  permissions: CoachPermissions
  thread: CoachThread | null
  messages: CoachMessage[]
  cursor: string | null
  pending_send?: { data: CoachSend } | null
}

/** Unsaved draft or execution editor state sent along with a coach question. */
export interface CoachDraft { programs?: string[]; prompt?: string; brief?: Partial<Brief>; rubric?: RubricCriterion[] }


/**
 * Verdict of the program's content checks on one uploaded state export, written by
 * backend/seldon_tasks/programs/. Advisory only: errors and warnings travel with the accepted
 * file and block nothing (decisions.md 2026-09-21). `skipped` means the program has no checks yet.
 */
export interface FileValidation {
  ok: boolean
  format: string
  records: number
  errors: string[]
  warnings?: string[]
  codes?: string[]
  skipped?: string
  summary?: {
    fusion_version?: string | null
    os?: string | null
    hub?: string | null
    project?: string | null
    doc?: string | null
    units?: string | null
    session_seconds?: number
    command_count?: number
    model_command_count?: number
    save_count?: number
    checkpoint_count?: number
    timeline_count?: number
    feature_sequence?: string[]
    body_count?: number
    dimensions?: { name?: string; expr?: string; unit?: string; role?: string; created_by?: string }[]
    f3d_sha256?: string | null
    checkpoint?: string | null
    clean_end?: boolean
  }
}

// ----------------------------------------------------------------- automatic task deployment

export type AutomationStepStatus =
  | 'pending' | 'running' | 'done' | 'failed' | 'skipped'
  // Parked on the coding agent: the step is not finished and nothing is wrong.
  | 'waiting'

export interface AutomationStep {
  key: string
  label: string
  status: AutomationStepStatus
  detail?: string
  started_at?: string | null
  finished_at?: string | null
  seconds?: number
  data?: Record<string, unknown>
}

export interface AutomationRun {
  run_id: string
  task_id: string
  status: 'queued' | 'running' | 'waiting_agent' | 'succeeded' | 'failed'
  program?: string
  splits: string[]
  notes?: string
  deriver?: string
  repo?: string
  dry_run?: boolean
  started_by_email?: string
  started_at: string
  finished_at?: string | null
  error?: string | null
  steps: AutomationStep[]
  review_notes?: string[]
  agent_job_id?: string | null
  waiting_since?: string | null
  resumes?: number
  result?: {
    s3_prefix?: string
    tree_sha256?: string
    provisional?: boolean
    profile?: string | null
    pr_url?: string
    pr_number?: number | null
    branch?: string
    commit?: string
    base_commit?: string
    counts?: Record<string, [number, number]>
    dry_run?: boolean
    review_notes?: string[]
    golden?: Record<string, unknown>
    golden_rationale?: string
    golden_placeholder?: boolean
    build_recorded?: boolean
    forwarded?: boolean
    to_stage?: number
    agent_job_id?: string
    agent_outcome?: 'covered' | 'needs_verifier'
    agent_coverage?: AgentCoverage[]
    agent_issues?: string[]
    agent_self_check?: { command?: string; ran?: boolean; clean?: boolean; output_tail?: string }
    validators?: {
      url: string
      number?: number | null
      branch: string
      title: string
      files: string[]
    }
  }
}

/** One line of what the coding agent did on the machine, as the Auto page shows it. */
export interface AgentAction {
  kind: 'message' | 'thought' | 'command' | 'file' | 'search' | 'todo' | 'note' | 'error'
  text: string
  at?: string
  meta?: Record<string, unknown>
}

/** One rubric criterion beside the evaluator that grades it. Joined by the backend from the
 *  task's own rubric, so a criterion the agent never mentioned is present with no verifier. */
export interface AgentCoverage {
  criterion?: string
  criterion_id?: string
  role?: string
  verifier?: string
  how?: string
  covered?: boolean
  /** False when the agent never mentioned this criterion at all, as opposed to saying that
   *  nothing grades it. The second is an answer; the first is a hole. */
  answered?: boolean
  in_rubric?: boolean
}

export interface AgentView {
  job: {
    job_id: string
    task_id: string
    run_id: string
    status: 'queued' | 'running' | 'succeeded' | 'failed' | 'expired'
    profile?: string | null
    worker?: string
    error?: string | null
    created_at?: string
    claimed_at?: string | null
    finished_at?: string | null
    last_event_at?: string | null
    skills_sha?: string
    counts?: { actions?: number; batches?: number; truncated?: boolean }
  }
  result?: Record<string, unknown>
  actions: AgentAction[]
  next: number
  live: boolean
  stale: boolean
}

export interface AutomationAvailability {
  enabled: boolean
  missing: string[]
  reason: string
  repo: string
  dry_run: boolean
  deriver: string
  intake: string
}

export interface AutomationView {
  availability: AutomationAvailability
  plan: { key: string; label: string; purpose: string }[]
  runs: AutomationRun[]
  active_run_id: string | null
  can_start: boolean
  splits: string[]
}
